import { useEffect, useState } from 'react';
import { DadosListagemContasReceberCliente } from '../../types/ContaReceberTypes';
import contaReceberApi from '../../api/apiContaReceber';
import { toast } from 'react-toastify';
import LinearProgress from '@mui/joy/LinearProgress';
import { makeStyles, Grid, Box, Typography } from '@material-ui/core';
import DescriptionIcon from '@mui/icons-material/Description';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { copyToClipboard, formatarDinheiro } from '../../helper';
import { Button } from '@mui/material';
import { useSessao } from '../../redux/sessao/sessaoHooks';
import { MENSAGEM_RETORNO_POP_UP_BLOQUEADO } from '../../constants';
import ModalPopupBloqueado from '../../Components/generic/ModalPopupBloqueado';

type Props = {
  onClose: () => void;
  dados: DadosListagemContasReceberCliente;
};

const StepBoleto: React.FC<Props> = (props) => {
  const { onClose, dados } = props;
  const classes = useStyles();
  const [gerandoBoleto, setGerandoBoleto] = useState(false);
  const [contaReceber, setContaReceber] = useState<DadosListagemContasReceberCliente | null>(null);
  const [loading, setLoading] = useState(true);
  const [popupBloqueado, setPopupBloqueado] = useState(false);
  const [sessao] = useSessao();

  useEffect(() => {
    buscarContaReceber();
  }, []);

  const buscarContaReceber = async () => {
    try {
      setLoading(true);

      const res = await contaReceberApi.getMany({
        where: {
          codigoEmpresa: dados.codigoEmpresa,
          codigoCliente: dados.codigoCliente,
          financeira: dados.financeira,
          fatura: dados.fatura
        },
        relations: ['empresa', 'cliente'],
        take: 1
      });

      if (!res || res.length === 0) {
        toast.error('Nenhuma fatura encontrada.');
        onClose();
        return;
      }

      setContaReceber(res[0]);
    } catch (error: any) {
      console.error(error);
      toast.error('Falha ao buscar a fatura.');
    } finally {
      setLoading(false);
    }
  };

  const gerarBoleto = async () => {
    try {
      setGerandoBoleto(true);

      await contaReceberApi.gerarBoleto({
        codigoEmpresa: dados.codigoEmpresa,
        codigoCliente: dados.codigoCliente,
        financeira: dados.financeira,
        fatura: dados.fatura
      });

      toast.success('Boleto gerado com sucesso.');
    } catch (error: any) {
      console.error(error);
      if (error?.message === MENSAGEM_RETORNO_POP_UP_BLOQUEADO) {
        setPopupBloqueado(true);
      } else {
        toast.error('Não foi possível gerar o boleto.');
      }
    } finally {
      await buscarContaReceber();
      setGerandoBoleto(false);
    }
  };

  const copiarLinhaDigitavel = async () => {
    if (!contaReceber) {
      toast.error('Fatura não encontrada.');
      return;
    }

    const linhaDigitavel = contaReceber?.linhaDigitavel?.trim();

    if (!linhaDigitavel) {
      toast.error('Linha digitável não disponível.');
      return;
    }

    copyToClipboard(contaReceber?.linhaDigitavel);
    toast.success('Código de barras copiado!');
  };

  const clientePodeEmitirBoleto = sessao?.empresa?.cfgEdCliente?.clientePodeEmitirBoleto ?? false;
  const temCodigoBarras = Boolean(contaReceber?.codigoBarrasBoleto?.trim()?.length);
  const temLinhaDigitavel = Boolean(contaReceber?.linhaDigitavel?.trim()?.length);

  return (
    <>
      <ModalPopupBloqueado open={popupBloqueado} onClose={() => setPopupBloqueado(false)} />

      {gerandoBoleto && (
        <div>
          <h1 style={{ textAlign: 'center' }}>Gerando boleto...</h1>
          <LinearProgress color="primary" size="lg" variant="soft" />
        </div>
      )}

      {loading && (
        <div>
          <h1 style={{ textAlign: 'center' }}>Carregando...</h1>
          <LinearProgress color="primary" size="lg" variant="soft" />
        </div>
      )}

      {!gerandoBoleto && contaReceber && (
        <>
          <Box className={classes.boxIcon}>
            <DescriptionIcon style={{ fontSize: 80, color: '#3498db' }} />
          </Box>

          <Typography variant="h5" align="center">
            Valor do boleto
          </Typography>

          <Typography variant="h4" align="center" gutterBottom>
            R$ {formatarDinheiro(contaReceber?.valores?.restante || 0)}
          </Typography>

          <Box className={classes.warningBox}>
            <WarningAmberIcon style={{ marginRight: 8 }} />
            <Typography variant="body2">
              {!temCodigoBarras && !clientePodeEmitirBoleto
                ? 'Pagamento não disponível. Por favor, entre em contato com a empresa.'
                : '  O pagamento via boleto pode levar algum tempo para ser compensado após o pagamento.'}
            </Typography>
          </Box>

          <Grid container direction="column" spacing={1} style={{ marginTop: '2rem' }}>
            {(temCodigoBarras || clientePodeEmitirBoleto) && (
              <Grid item xs={12}>
                <Button variant="contained" fullWidth onClick={gerarBoleto}>
                  {temCodigoBarras ? 'Download boleto' : 'Gerar boleto'}
                </Button>
              </Grid>
            )}

            {temCodigoBarras && temLinhaDigitavel && (
              <Grid item xs={12}>
                <Button variant="contained" fullWidth onClick={copiarLinhaDigitavel}>
                  Copiar código de barras
                </Button>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </>
  );
};

export default StepBoleto;

const useStyles = makeStyles({
  boxIcon: {
    display: 'flex',
    justifyContent: 'center',
    margin: '2rem 0'
  },
  warningBox: {
    display: 'flex',
    alignItems: 'flex-start',
    backgroundColor: '#FFF3CD',
    padding: '1rem',
    borderRadius: '8px',
    marginTop: '1.5rem',
    color: '#856404'
  }
});
